import { uniqueId } from 'lodash';

import { createSelector } from '@reduxjs/toolkit';

import { ProductType } from '~/core/enums';
import {
  ApplianceModel,
  ApplianceSource,
  ApplianceType,
  CutoutLaborModel,
} from '~/core/models';
import {
  addMockImage,
  convertProductsToProductDetailsList,
} from '~/core/utils';

/**
 * Customer Supplied Sink APIs has a different model structure, if there isn't `priceWithoutDiscount` then it's a customer supplied sink
 * @param sink
 * @returns true if sink is a browsed sink as opposed to a customer supplied sink
 */
export const isBrowsedSink = (sink: ApplianceModel): boolean =>
  !!sink.priceWithoutDiscount;

export const isCustomerSuppliedSink = (sink: ApplianceModel): boolean =>
  !isBrowsedSink(sink);

export const convertSinksToProductDetails = convertProductsToProductDetailsList(
  ProductType.SINK
);
export const convertSinkLaborToProductDetails =
  convertProductsToProductDetailsList(ProductType.SINK_LABOR);

/**
 * Corrects a given productSourceValue case
 * @param originalProductSource
 * @param correctProductSource
 */
const fixProductSourceCase = (
  originalProductSource: string,
  correctProductSource: string
) =>
  originalProductSource
    ? originalProductSource.replace(
        new RegExp(correctProductSource, 'gi'),
        correctProductSource
      )
    : correctProductSource;

type SelectorCallback<T> = (
  data: ApplianceModel[],
  productSource: ApplianceSource,
  applianceType?: ApplianceType,
  shouldAddProductSourceToFeatures?: boolean
) => T;

export const makeSinksSelector = () =>
  createSelector<
    [
      SelectorCallback<ApplianceModel[]>,
      SelectorCallback<ApplianceSource>,
      SelectorCallback<ApplianceType | undefined>,
      SelectorCallback<boolean | undefined>,
    ],
    ApplianceModel[]
  >(
    [
      (data) => data,
      (_, productSource) => productSource,
      (_, __, applianceType) => applianceType,
      (_, __, ___, shouldAddProductSourceToFeatures) =>
        shouldAddProductSourceToFeatures,
    ],
    (
      data,
      productSource,
      applianceType,
      shouldAddProductSourceToFeatures = false
    ) =>
      data
        ?.map((sink) => ({
          ...sink,
          uuid: sink.uuid ?? uniqueId(),
          productSource,
          applianceType: sink.applianceType ?? applianceType,
          features: shouldAddProductSourceToFeatures
            ? [
                ...(sink.features ?? []),
                {
                  name: 'productSource',
                  description: 'Product Source',
                  position: 12,
                  value: sink.productSource // TODO Should we just use the given parameter productSource and ignore what the API provides?
                    ? fixProductSourceCase(
                        sink.productSource,
                        ApplianceSource.PROGRAM
                      ) // fix the case on program products
                    : productSource,
                },
              ]
            : (sink.features ?? []), //TODO: sink.features in data may actually be undefined despite current typing saying otherwise. Fixing types required
        }))
        ?.map(addMockImage)
  );

/**
 * Defining empty array outside of selector as indicated in docs.
 * @see {@link https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#selecting-values-from-results}
 */
export const emptyArray = [] satisfies ApplianceModel[];

interface SinkPricesResult {
  /**The price of the sink without discount. Only sink not the cutout labor */
  sinkPrice: number;
  /** The sink price only. Discounts are applied if applicable. */
  sinkPriceWithDiscount: number | undefined;
  /** The cutoutLaborPrice price only. */
  cutoutLaborPrice: number;
  /** The total price including both the sink price and cutout labor price without discounts. */
  sinkAndCutoutLaborPrice: number;
  /**
   * The total price including both the sink and cutout labor price.
   * Discounts are applied if applicable.
   */
  sinkAndCutoutLaborPriceWithDiscount: number;
}

/**
 * Returns the prices of a sink and its cutout labor.
 * @param sink - The sink appliance model.
 * @param cutoutLaborProduct - The cutout labor appliance model or cutout labor model.
 * @returns An object containing the prices of the sink and cutout labor.
 * @see {@link SinkPricesResult}
 */
export const getSinkPrices = (
  sink: ApplianceModel,
  cutoutLaborProduct: ApplianceModel | CutoutLaborModel
): SinkPricesResult => {
  const cutoutLaborPrice = cutoutLaborProduct?.price ?? 0;

  const hasDiscount =
    !!sink.discountPercentage &&
    !!sink.priceWithoutDiscount &&
    sink.price !== sink.priceWithoutDiscount;
  const sinkPriceWithDiscount = isCustomerSuppliedSink(sink)
    ? undefined
    : hasDiscount || !sink.priceWithoutDiscount
      ? sink.price
      : sink.priceWithoutDiscount;
  const sinkAndCutoutLaborPriceWithDiscount =
    (sinkPriceWithDiscount ?? 0) + cutoutLaborPrice;
  const sinkAndCutoutLaborPrice =
    (sink.priceWithoutDiscount ?? 0) + cutoutLaborPrice;

  return {
    sinkPrice: sink.priceWithoutDiscount,
    sinkPriceWithDiscount,
    cutoutLaborPrice,
    sinkAndCutoutLaborPrice,
    sinkAndCutoutLaborPriceWithDiscount,
  };
};
